// ShadowGuard API Client — aligned with control plane routes

/** Default fetch timeout. Fleet/status integrity scans can exceed 10s under load. */
const DEFAULT_TIMEOUT = 30000;
const FLEET_TIMEOUT = 60000;

interface ApiConfig {
  baseUrl: string;
  token?: string;
}

export interface DashboardStats {
  activeSessions: number;
  activeThreats: number;
  threatsDetected: number;
  deceptionsServed: number;
  /** Decoys with time_wasted_ms > 0 (time burned) */
  deceptionsEngaged: number;
  /**
   * CANONICAL deep callbacks — non-synthetic follow hops from engagement_hops
   * (route_follow / canary_follow / chain_follow; decoy_serve excluded).
   * Genuinely rare: expect tens, not thousands.
   */
  deepCallbacks?: number;
  /** Deepest ops-chain stage reached in window (0 = nothing followed). */
  maxEngagementDepth?: number;
  /** Distinct sessions with at least one follow hop. */
  deepCallbackSessions?: number;
  /** Follow hops at depth >= 2 — multi-stage chain follows. */
  multiStageCallbacks?: number;
  /**
   * @deprecated LEGACY — deceptions.engaged = 1. The July ops-chain fast path
   * does not write it, so it undercounts. Read `deepCallbacks` instead.
   * See docs/research/B1_DEEP_CALLBACK_PROBE.md.
   */
  deceptionsCallbacks?: number;
  timeWastedMs: number;
  fingerprintsTracked: number;
  fingerprintsCollected: number;
  stixBundlesGenerated: number;
  avgConfidence: number;
  canaryHits?: number;
  /** Distinct actors (IP+15m+soft UA clusters). Synthetic excluded. */
  actorClusters?: number;
  timeline: Array<{ time: string; sessions: number; threats: number; deceptions: number; fingerprints: number }>;
}

export interface ActorCluster {
  id: string;
  sourceIp: string;
  firstSeen: string;
  lastSeen: string;
  sessionCount: number;
  requestCount: number;
  maxConfidence: number;
  maxDepth: number;
  fingerprintIds: string[];
  userAgents: string[];
  techniques: string[];
  isSynthetic: boolean;
  primarySessionId: string | null;
  primaryFingerprintId: string | null;
  /** From member sessions (most recent non-empty) */
  sourceCity?: string;
  sourceCountry?: string;
  sourceAsn?: number | null;
  sourceAsOrg?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ActorSessionDetail {
  id: string;
  fingerprintId: string | null;
  sourceIp?: string;
  userAgent: string;
  requestCount: number;
  firstSeen: string;
  lastSeen: string;
  isSynthetic: boolean;
  confidence?: number;
  threatLevel?: string;
  techniques?: string[];
  sourceCity?: string;
  sourceCountry?: string;
  sourceAsn?: number | null;
  sourceAsOrg?: string;
}

export interface CanaryHit {
  id: string;
  sessionId: string;
  canaryId: string;
  deceptionId: string;
  canaryPath: string;
  deltaMs: number;
  timingBand: 'sub_2s' | '2s_to_30s' | 'over_30s';
  timingLabel: string;
  sourceIp: string | null;
  sourceAsn: number | null;
  sourceAsOrg: string | null;
  userAgent: string | null;
  headerOrder: string[];
  isSynthetic: boolean;
  breadcrumbsBefore: number;
  breadcrumbsAfter: number;
  decoyPath: string | null;
  decoyVariant: string | null;
  signalType: string;
  confidence: number;
  createdAt: string;
}

export interface ThreatFeedItem {
  id: string;
  sessionId: string;
  sourceIp: string;
  level: 'none' | 'low' | 'medium' | 'high' | 'critical';
  confidence: number;
  techniques: string[];
  timestamp: string;
  requestCount: number;
  deceptionEngaged: boolean;
  userAgent: string;
  sourceCountry: string;
  sourceCity: string;
  sourceAsn: number | null;
  sourceAsOrg: string;
  ja3Hash: string | null;
  ja4Hash: string | null;
  // Attribution fields
  attribution?: string;
  isTor?: boolean;
  isCloud?: boolean;
  cloudProvider?: string;
  isKnownScanner?: boolean;
  scannerName?: string;
  // SGIS (ShadowGuard Intelligence Score) fields
  sgisScore?: number;                    // 0-100 composite threat score
  honeypotTriggered?: boolean;          // Honeypot path accessed
  datacenterTraffic?: boolean;          // Traffic from cloud/datacenter ASN
  headerAnomalies?: string[];           // List of detected header anomalies
  requestsPerMinute?: number;           // Request velocity metric
}

export interface Session {
  id: string;
  sourceIp: string;
  userAgent: string;
  threatLevel: string;
  confidence: number;
  requestCount: number;
  firstSeen: string;
  lastSeen: string;
  isActive: boolean;
  sourceCountry: string;
  sourceCity: string;
}

export interface Fingerprint {
  id: string;
  ja3Hash: string | null;
  ja4Hash: string | null;
  sessionCount: number;
  clusterIds: string[];
  firstSeen: string;
  lastSeen: string;
  browserFamily: string | null;
  osFamily: string | null;
}

export interface Deception {
  id: string;
  session_id: string; // API returns snake_case
  tenant_id: string;
  deception_type: 'honeypot' | 'breadcrumb' | 'tarpit' | 'micro-delay';
  variant?: string | null;
  path: string;
  engaged: number; // API returns 0 or 1
  engaged_at: string | null;
  captured_data_json: string | null;
  time_wasted_ms: number; // API returns snake_case
  breadcrumbs_followed: number; // API returns snake_case
  created_at: string; // API returns snake_case
  depth: number;
  parent_deception_id: string | null;
  traffic_source?: string;
  policy_applied?: string | null;
}

export interface DeceptionInteraction {
  id: string;
  deception_id: string;
  session_id: string;
  interaction_type: 'pixel_load' | 'page_view' | 'form_submit' | 'input_focus' | 'download' | 'api_call' | 'breadcrumb_follow';
  interaction_data_json: string;
  timestamp: string;
  deception_type: string;
  variant?: string;
  path: string;
  source_ip: string;
  source_country: string;
  threat_level: string;
}

export interface EngagementFunnel {
  total_served: number;
  total_accessed: number;
  total_interacted: number;
  interaction_types_count: number;
}

export interface InteractionTypeBreakdown {
  interaction_type: string;
  count: number;
}

export interface ThreatAssessment {
  id: string;
  sessionId: string;
  level: string;
  confidence: number;
  techniques: string[];
  evidence: string[];
  correlationType: string;
  createdAt: string;
}

export interface StixBundle {
  id: string;
  threatActorId: string;
  objectCount: number;
  createdAt: string;
}

export interface AttackerProfile {
  id: string;
  fingerprint_id: string;
  tenant_id: string;
  threat_actor_id?: string;
  campaign_id?: string;
  first_seen: string;
  last_seen: string;
  session_count: number;
  source_ips: string[];
  source_countries: string[];
  source_asns: number[];
  user_agents: string[];
  attack_phases: ('recon' | 'exploit' | 'persist')[];
  techniques_used: string[];
  tools_detected: string[];
  evasion_techniques: string[];
  payloads_attempted: number;
  sophistication_score: number;
  automation_detected: boolean;
  deceptions_engaged: number;
  time_wasted_total_ms: number;
  credentials_attempted: number;
  confidence: number;
  created_at: string;
  updated_at: string;
}

export interface IntelStats {
  active_profiles: number;
  unique_tools: number;
  campaigns: number;
  high_confidence_iocs: number;
}

export interface Tool {
  tool: string;
  count: number;
}

export interface ThreatActor {
  id: string;
  name: string;
  fingerprint_ids: string[];
  techniques: string[];
  infrastructure: Record<string, any>;
  first_seen: string;
  last_seen: string;
  session_count: number;
  confidence: number;
  notes: string;
}

export interface Agent {
  id: string;
  name: string;
  runtime: string;
  status: 'active' | 'inactive' | 'degraded';
  version: string;
  lastHeartbeat: string | null;
  lastIngestOkAt?: string | null;
  monitorState?: 'healthy' | 'stale' | 'dark' | 'never_seen' | 'degraded';
  monitorCause?: string | null;
  monitorDetail?: string | null;
  domain?: string | null;
  encryptionKeyConfigured?: number | null;
  deployedAt?: string | null;
}

export interface FleetAgentStatus {
  agentId: string;
  tenantId: string;
  name: string;
  runtime: string;
  domain: string | null;
  state: 'healthy' | 'quiet' | 'stale' | 'dark' | 'never_seen' | 'degraded';
  cause: string | null;
  detail: string | null;
  lastSeenAt: string | null;
  lastIngestOkAt: string | null;
  ageMs: number | null;
  previouslyReported: boolean;
  encryptionKeyConfigured: boolean | null;
  counters: { authFailures: number; decryptFailures: number; ingestErrors: number };
  activity: { sessions1h: number; sessions24h: number; deceptions1h: number; deceptions24h: number };
}

export interface FleetIntegrityFailure {
  code: string;
  severity: 'warning' | 'critical';
  message: string;
  value: number;
  threshold: number;
}

export interface FleetStatus {
  ok: boolean;
  computedAt: string;
  summary: {
    total: number;
    healthy: number;
    quiet?: number;
    stale: number;
    dark: number;
    never_seen: number;
    degraded: number;
    integrityFailing: boolean;
    /** Warning-level integrity (does not force fleet not-ok / 503). */
    integrityWarnings?: boolean;
  };
  agents: FleetAgentStatus[];
  integrity: {
    windowMinutes: number;
    sessionsN: number;
    nullFingerprintN: number;
    nullFingerprintPct: number;
    deceptionsN: number;
    brokenParentN: number;
    orphanDeceptionN: number;
    failing: boolean;
    hasWarnings?: boolean;
    failures: FleetIntegrityFailure[];
    interceptGapAgents: Array<{ agentId: string; name: string; sessions: number; deceptions: number }>;
  };
  recentAlerts: Array<{
    id: string;
    agentId: string | null;
    alertType: string;
    severity: string;
    fromState: string | null;
    toState: string | null;
    cause: string | null;
    message: string;
    createdAt: string;
  }>;
}

export interface UsageData {
  tenant: {
    id: string;
    name: string;
    tier: string;
    maxAgents: number;
    createdAt: string;
    isActive: boolean;
  };
  usage: {
    daily: {
      current: number;
      limit: number;
      remaining: number;
      percentUsed: number;
    };
    monthly: {
      total: number;
      peakDay: number;
      dailyBreakdown: Array<{ date: string; count: number }>;
    };
  };
  plans: Array<{
    id: string;
    name: string;
    price: number | null;
    eventsPerDay: number | null;
    features: Record<string, boolean>;
    upgradeUrl: string | null;
  }>;
  currentPlan: {
    canUpgrade: boolean;
    suggestedPlan: string | null;
  };
}

export interface HoneypotSuggestion {
  suggested_path: string;
  suggested_variant: string;
  confidence_score: number;
  request_frequency: number;
  unique_attackers: number;
  estimated_effectiveness: number;
  reason: string;
  created_at: string;
}

export interface AttackPatternAnalysis {
  top_paths: Array<{ pattern: string; requests: number; category: string }>;
  category_breakdown: Record<string, number>;
  unserved_high_priority: Array<{ pattern: string; priority: number; failed_responses: number }>;
  insights: {
    most_targeted_category: string;
    coverage_gaps: number;
    total_patterns: number;
  };
}

export interface PathIntelligence {
  normalized_pattern: string;
  attack_category: string;
  total_requests: number;
  unique_sessions: number;
  successful_responses: number;
  failed_responses: number;
  deceptions_served: number;
  deceptions_engaged: number;
  avg_confidence: number;
  priority_score: number;
  first_seen: string;
  last_seen: string;
  honeypot_variant: string | null;
}

export interface LoginResponse {
  token: string;
  tenant: { id: string; name: string; tier: string };
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ShadowGuardApi {
  private config: ApiConfig;

  constructor(baseUrl: string, token?: string) {
    this.config = { baseUrl: baseUrl.replace(/\/$/, ''), token };
  }

  setToken(token: string) { this.config.token = token; }
  getBaseUrl() { return this.config.baseUrl; }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(this.config.token ? { Authorization: `Bearer ${this.config.token}` } : {}),
        ...(options.headers as Record<string, string>) || {},
      };

      const res = await fetch(`${this.config.baseUrl}${path}`, {
        ...options, headers, signal: controller.signal,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        const errorMessage = res.status === 429 
          ? 'Too many requests. Please wait a moment and try again.'
          : (body.error || res.statusText);
        throw new ApiError(res.status, errorMessage);
      }

      return res.json() as Promise<T>;
    } finally {
      clearTimeout(timeout);
    }
  }

  async login(apiKey: string): Promise<LoginResponse> {
    return this.request('/api/v1/auth/login', {
      method: 'POST', body: JSON.stringify({ apiKey }),
    });
  }

  async getUsage(): Promise<UsageData> {
    return this.request('/api/v1/tenant-settings/usage');
  }

  async getStats(hours?: number): Promise<DashboardStats> {
    const query = hours ? `?hours=${hours}` : '';
    return this.request(`/api/v1/dashboard/stats${query}`);
  }

  async getHistoricalTrends(days?: number): Promise<{
    period: { days: number; start: string };
    dailyStats: Array<{ date: string; sessions: number; threats: number; avg_confidence: number }>;
    topAttackers: Array<{ source_ip: string; session_count: number; max_confidence: number; max_threat_level: string; country: string }>;
    topTechniques: Array<{ technique: string; count: number }>;
    deceptionTrends: Array<{
      date: string;
      served: number;
      /** Decoys that applied any delay (time_wasted_ms > 0) — the real Time Burned series. */
      time_burned?: number;
      /** Hop-based deep callbacks for that day (follow kinds, non-synthetic). */
      deep_callbacks?: number;
      /** Deepest chain stage reached that day. */
      max_engagement_depth?: number;
      /**
       * @deprecated LEGACY — deceptions.engaged = 1. Never label this
       * "Time Burned"; Reports did and showed ~0 for tenants whose real burn
       * rate was >95%. Read via resolveTimeBurnedDaily().
       */
      engaged?: number;
      time_wasted: number;
    }>;
    threatLevelTrends: Array<{ date: string; none: number; low: number; medium: number; high: number; critical: number }>;
  }> {
    const query = days !== undefined ? `?days=${days}` : '';
    return this.request(`/api/v1/dashboard/trends${query}`);
  }

  async getThreatFeed(limit?: number): Promise<{ threats: ThreatFeedItem[] }> {
    const query = limit ? `?limit=${limit}` : '';
    return this.request(`/api/v1/dashboard/live-threats${query}`);
  }

  async getSessions(active?: boolean): Promise<{ sessions: Session[] }> {
    const query = active !== undefined ? `?active=${active}` : '';
    return this.request(`/api/v1/sessions${query}`);
  }

  async getFingerprints(): Promise<{ fingerprints: Fingerprint[] }> {
    return this.request('/api/v1/fingerprints');
  }

  async getActors(opts?: { includeSynthetic?: boolean; limit?: number }): Promise<{
    actors: ActorCluster[];
    total: number;
  }> {
    const params = new URLSearchParams();
    if (opts?.includeSynthetic) params.set('include_synthetic', '1');
    if (opts?.limit) params.set('limit', String(opts.limit));
    const q = params.toString() ? `?${params}` : '';
    return this.request(`/api/v1/actors${q}`);
  }

  async getActor(id: string): Promise<{
    actor: ActorCluster;
    sessions: ActorSessionDetail[];
  }> {
    return this.request(`/api/v1/actors/${id}`);
  }

  async getDeceptions(): Promise<{ deceptions: Deception[] }> {
    return this.request('/api/v1/deceptions');
  }

  async getCanaryHits(limit = 50): Promise<{ hits: CanaryHit[]; total: number }> {
    return this.request(`/api/v1/canaries/hits?limit=${limit}`);
  }

  async getCanaryHitDetail(id: string): Promise<{ hit: CanaryHit; timeline: unknown[] }> {
    return this.request(`/api/v1/canaries/hits/${id}`);
  }

  async getCanaryStats(hours = 720): Promise<{
    total: number;
    inWindow: number;
    windowHours: number;
    byBand: Record<string, number>;
  }> {
    return this.request(`/api/v1/canaries/stats?hours=${hours}`);
  }

  async getDeceptionInteractions(options?: { sessionId?: string; deceptionId?: string; type?: string; limit?: number }): Promise<{
    interactions: DeceptionInteraction[];
    funnel: EngagementFunnel;
    typeBreakdown: InteractionTypeBreakdown[];
  }> {
    const params = new URLSearchParams();
    if (options?.sessionId) params.append('session_id', options.sessionId);
    if (options?.deceptionId) params.append('deception_id', options.deceptionId);
    if (options?.type) params.append('type', options.type);
    if (options?.limit) params.append('limit', options.limit.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/api/v1/deceptions/interactions${query}`);
  }

  async getThreats(): Promise<{ threats: ThreatAssessment[] }> {
    return this.request('/api/v1/threats');
  }

  async getStixBundles(): Promise<{ bundles: StixBundle[] }> {
    return this.request('/api/v1/stix/bundles');
  }

  async getAgents(): Promise<{ agents: Agent[]; tenantStats?: Record<string, unknown> }> {
    return this.request('/api/v1/agents');
  }

  async getFleetStatus(): Promise<FleetStatus> {
    // 503 when fleet not ok — body still contains full status (not an auth failure).
    // Use a longer timeout: integrity scans have been measured at 15–90s under load;
    // the previous 10s default aborted and showed "Fleet status unreachable" while
    // lighter endpoints (live-threats) still succeeded.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), FLEET_TIMEOUT);
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(this.config.token ? { Authorization: `Bearer ${this.config.token}` } : {}),
      };
      const res = await fetch(`${this.config.baseUrl}/api/v1/fleet/status`, {
        headers,
        signal: controller.signal,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok && res.status !== 503) {
        throw new ApiError(res.status, (body as { error?: string }).error || res.statusText);
      }
      return body as FleetStatus;
    } finally {
      clearTimeout(timeout);
    }
  }

  async createAgent(name: string, runtime: string): Promise<Agent> {
    return this.request('/api/v1/agents', {
      method: 'POST', body: JSON.stringify({ name, runtime }),
    });
  }

  // Intelligence API
  async getIntelStats(): Promise<IntelStats> {
    return this.request('/api/v1/intel/stats');
  }

  async getAttackerProfiles(minConfidence = 50, limit = 50): Promise<{ profiles: AttackerProfile[]; total: number }> {
    return this.request(`/api/v1/intel/attacker-profiles?min_confidence=${minConfidence}&limit=${limit}`);
  }

  async getAttackerProfile(id: string): Promise<{ profile: AttackerProfile; sessions: Session[]; deceptions: Deception[] }> {
    return this.request(`/api/v1/intel/attacker-profiles/${id}`);
  }

  async getDetectedTools(): Promise<{ tools: Tool[]; total_unique: number; total_detections: number; profiles_analyzed: number }> {
    return this.request('/api/v1/intel/tools');
  }

  async getThreatActors(limit = 50): Promise<{ threat_actors: ThreatActor[]; total: number }> {
    return this.request(`/api/v1/intel/threat-actors?limit=${limit}`);
  }

  // Adaptive Intelligence API
  async getHoneypotSuggestions(limit = 10): Promise<{ suggestions: HoneypotSuggestion[]; total: number; message: string }> {
    return this.request(`/api/v1/intel/adaptive/suggestions?limit=${limit}`);
  }

  async getAttackPatternAnalysis(): Promise<AttackPatternAnalysis> {
    return this.request('/api/v1/intel/adaptive/attack-patterns');
  }

  async getPathIntelligence(minPriority = 0, limit = 50): Promise<{ paths: PathIntelligence[]; total: number }> {
    return this.request(`/api/v1/intel/adaptive/path-intelligence?min_priority=${minPriority}&limit=${limit}`);
  }

  async implementSuggestion(suggestedPath: string): Promise<{ success: boolean; message: string }> {
    return this.request('/api/v1/intel/adaptive/implement-suggestion', {
      method: 'POST',
      body: JSON.stringify({ suggested_path: suggestedPath }),
    });
  }
}
